import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { X, Volume2, BookOpen } from 'lucide-react';
import { textsApi } from '../utils/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Split text into sentence chunks preserving punctuation. */
function splitSentences(text) {
  if (!text) return [];
  // Split on sentence-ending punctuation followed by whitespace or end
  const raw = text.split(/(?<=[.!?؟।。！？])\s+/);
  // Merge very short fragments (< 4 chars) with the previous sentence
  const merged = [];
  for (const s of raw) {
    const clean = s.trim();
    if (!clean) continue;
    if (clean.length < 4 && merged.length > 0) {
      merged[merged.length - 1] += ' ' + clean;
    } else {
      merged.push(clean);
    }
  }
  return merged;
}

/** Derive a plain language code from source_language_id (e.g. "lang_es" → "es"). */
function langCode(text) {
  const id = text?.source_language_id || text?.language_code || '';
  return id.replace(/^lang_/, '');
}

export default function TextViewer() {
  const { textId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLibrary = searchParams.get('source') === 'library';

  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playingIdx, setPlayingIdx] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetcher = isLibrary
      ? axios.get(`${BACKEND_URL}/api/library/text/${textId}`, { withCredentials: true })
      : textsApi.getById(textId);
    fetcher
      .then(r => setText(r.data))
      .catch(() => setError('Could not load text.'))
      .finally(() => setLoading(false));
  }, [textId, isLibrary]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingIdx(null);
  }, []);

  const playAudio = useCallback((sentence, idx) => {
    // Clicking the same button while playing = stop
    if (playingIdx === idx) { stopAudio(); return; }
    stopAudio();

    const lang = langCode(text);
    const url = `${BACKEND_URL}/api/tts/speak?text=${encodeURIComponent(sentence)}&lang=${encodeURIComponent(lang)}`;
    const audio = new Audio(url);
    audio.crossOrigin = 'use-credentials';
    audioRef.current = audio;
    setPlayingIdx(idx);

    audio.onended = () => { setPlayingIdx(null); audioRef.current = null; };
    audio.onerror = (e) => {
      if (audio.error?.code === 1) return; // user aborted
      setPlayingIdx(null);
      audioRef.current = null;
    };
    audio.play().catch(() => { setPlayingIdx(null); audioRef.current = null; });
  }, [playingIdx, text, stopAudio]);

  // Cleanup on unmount
  useEffect(() => () => stopAudio(), [stopAudio]);

  if (loading) return (
    <div className="flex items-center justify-center h-screen text-muted-foreground">Loading…</div>
  );
  if (error || !text) return (
    <div className="flex items-center justify-center h-screen text-destructive">{error || 'Text not found.'}</div>
  );

  const sentences = splitSentences(text.content);
  const isRtl = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text.content);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header bar */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border flex items-center justify-between px-4 py-2 gap-2">
        <button
          onClick={() => navigate(`/reader/${textId}`)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="open-in-reader-btn"
        >
          <BookOpen className="h-4 w-4" />
          <span className="hidden sm:inline">Open in Reader</span>
        </button>
        <button
          onClick={() => navigate('/texts')}
          className="p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          data-testid="close-text-viewer-btn"
          title="Back to library"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-5 py-8">
        {/* Title row */}
        <div className="flex items-start gap-3 mb-8">
          <button
            onClick={() => playAudio(text.content.slice(0, 499), -1)}
            className={`mt-1 flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              playingIdx === -1
                ? 'text-amber-400 bg-amber-400/20 scale-105'
                : 'text-primary bg-primary/10 hover:bg-primary/20 hover:scale-105'
            }`}
            title={playingIdx === -1 ? 'Stop' : 'Play full text'}
            data-testid="play-full-text-btn"
          >
            <Volume2 className="h-4 w-4" />
            <span className="hidden sm:inline">{playingIdx === -1 ? 'Stop' : 'Listen'}</span>
          </button>
          <h1
            className="font-bold leading-tight"
            style={{ fontSize: 'calc(1rem * 1.44 * 2)' }}
            dir={isRtl ? 'rtl' : 'ltr'}
            data-testid="text-viewer-title"
          >
            {text.title}
          </h1>
        </div>

        {/* Body — sentence by sentence */}
        <div dir={isRtl ? 'rtl' : 'ltr'} className="space-y-3 text-xl leading-loose text-justify" data-testid="text-viewer-body">
          {sentences.map((sentence, idx) => (
            <span key={idx} className="inline">
              <button
                onClick={() => playAudio(sentence, idx)}
                className={`inline-flex items-center justify-center h-7 w-7 rounded-full mr-1.5 flex-shrink-0 transition-all align-middle ${
                  playingIdx === idx
                    ? 'text-amber-400 bg-amber-400/20 scale-110'
                    : 'text-primary bg-primary/10 hover:bg-primary/20 hover:scale-110'
                }`}
                title={playingIdx === idx ? 'Stop' : 'Play sentence'}
                data-testid={`play-sentence-${idx}-btn`}
              >
                <Volume2 className="h-4 w-4" />
              </button>
              <span>{sentence} </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
